import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { AtiraIcon } from '../../../components/AtiraIcon';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import { Breakpoints } from '../../../theme/Breakpoints';
import { FontSize } from '../../../theme/FontSize';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';

const Section = styled(Flex)`
  background-color: ${({ theme }) => theme.bgCanvas};
  border-radius: ${Rounded.lg};
  flex-direction: column;
  justify-content: space-between;
`;

const TitleWrapper = styled(Flex)`
  align-items: center;
  gap: ${Spacing.m};
  background: ${({ theme }) => `
  linear-gradient(90deg, ${theme.accent}  0%, ${theme.main} 100%)
  `};
  border-radius: ${Rounded.lg} ${Rounded.lg} 0 0;
  padding: ${Spacing.m};
  justify-content: space-between;
  color: ${({ theme }) => theme.onMain};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    justify-content: flex-start;
  }
`;

const SectionTitle = styled(Text)`
  font-weight: bold;
  color: ${({ theme }) => theme.onMain};
`;

const ActionsWrapper = styled(Flex)`
  border-radius: 0 0 ${Rounded.lg} ${Rounded.lg};
  justify-content: flex-end;
  overflow: hidden;
  flex-direction: row;

  button {
    border-radius: 0;
  }

  button span {
    display: none;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    button span {
      display: inline-block;
    }
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    flex-direction: row;

    button,
    a {
      width: 15rem;
    }
  }
`;

const InfoLink = styled(Link)`
  color: ${({ theme }) => theme.onSub};
  background-color: ${({ theme }) => theme.sub};
  text-decoration: none;
  padding: ${Spacing.s} ${Spacing.l};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  transition: all 0.3s;
  font-size: ${FontSize.m};

  span {
    display: none;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    span {
      display: inline-block;
    }
  }

  &:hover {
    color: ${({ theme }) => theme.onSub};
    filter: brightness(0.9);
    -webkit-filter: brightness(0.9);
  }
`;

type DashboardWidgetProps = {
  title: string;
  titleIcon: IconProp;
  children: React.ReactNode;
  mainAction: {
    title: React.ReactNode;
    icon: IconProp;
    onClick: VoidFunction;
  };
  moreActionLink?: string;
  showActions: boolean;
};

export const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  title,
  titleIcon,
  children,
  mainAction,
  moreActionLink,
  showActions = false,
}) => {
  const { t } = useTranslation();

  return (
    <Section>
      <TitleWrapper>
        <SectionTitle>{title}</SectionTitle>

        <AtiraIcon icon={titleIcon} size="xl" />
      </TitleWrapper>

      {children}
      {showActions ? (
        <ActionsWrapper>
          {moreActionLink ? (
            <InfoLink to={moreActionLink}>
              <AtiraIcon icon={faInfoCircle} />

              <span>{t('common.moreDetails')}</span>
            </InfoLink>
          ) : null}

          <Button
            onClick={mainAction.onClick}
            icon={mainAction.icon}
            hover
            hoverBackgroundColor="mainHover"
          >
            {mainAction.title}
          </Button>
        </ActionsWrapper>
      ) : null}
    </Section>
  );
};
